import { useGoogleLogin } from "@react-oauth/google";
import { FcGoogle } from "react-icons/fc";
import { useEffect, useContext, useState } from "react";
import { GetUserById, RecordComplianceAttestation } from "../services/backend";
import { establishSupabaseSessionFromGoogle } from "../services/supabase/authSession";
import { AuthContext } from "../store/auth-context";
import React from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { APP_FONT_FAMILY } from "../constants/typography";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  Heading,
  Link,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import "../styles/landing.css";
import LandingImage from "../components/LandingImage";
import {
  TERMS_VERSION,
  buildComplianceAttestationPayload,
  clearPendingCompliance,
  writePendingCompliance,
} from "../constants/datingCompliance";

const Landing = () => {
  const { t } = useTranslation();
  const AuthCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const toast = useToast();
  const [complianceAccepted, setComplianceAccepted] = useState(false);
  let loggedInUserId = null;
  try {
    const raw = localStorage.getItem("user");
    loggedInUserId = raw ? JSON.parse(raw)?.id ?? null : null;
  } catch {
    loggedInUserId = null;
  }

  const tagBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const tagBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const tagText = useColorModeValue("gray.800", "whiteAlpha.900");
  const headingColor = useColorModeValue("gray.900", "white");
  const serifWord = useColorModeValue("gray.900", "white");
  const subtext = useColorModeValue("gray.600", "whiteAlpha.700");
  const authCardBg = useColorModeValue("rgba(255,255,255,0.92)", "rgba(18, 18, 20, 0.65)");
  const authCardBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const authCardShadow = useColorModeValue(
    "0 20px 50px rgba(15,23,42,0.08)",
    "0 20px 60px rgba(0,0,0,0.45)"
  );
  const authLabel = useColorModeValue("gray.600", "whiteAlpha.600");
  const googleBtnBg = useColorModeValue("white", "rgba(255,255,255,0.06)");
  const googleBtnHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const googleBtnColor = useColorModeValue("gray.800", "white");
  const googleBtnBorder = useColorModeValue("gray.300", "whiteAlpha.300");
  const termsLinkColor = useColorModeValue("gray.600", "whiteAlpha.700");
  const headlineAccentGradient = useColorModeValue(
    "linear(to-r, brand.400, brand.600)",
    "linear(to-r, brand.300, brand.600)"
  );
  const liveDotShadow = useColorModeValue(
    "0 0 12px rgba(52, 211, 153, 0.45), 0 0 22px rgba(253, 224, 71, 0.25)",
    "0 0 14px rgba(253, 224, 71, 0.4), 0 0 26px rgba(16, 185, 129, 0.28)"
  );

  useEffect(() => {
    if (!loggedInUserId) return;
    navigate("/shop", { replace: true });
  }, [loggedInUserId, navigate]);

  async function completeLogin(accessToken) {
    try {
      const { profile } = await establishSupabaseSessionFromGoogle(accessToken);
      let userFromDb = profile;
      if (!userFromDb?.id) {
        // Session established; load profile by google sub from local exchange result
        throw new Error("Profile missing after login");
      }

      // Refresh mapped profile (includes images/interests when available)
      try {
        const fresh = await GetUserById(userFromDb.id);
        if (fresh) userFromDb = fresh;
      } catch (error) {
        console.warn("Profile refresh after login skipped:", error);
      }

      localStorage.setItem("user", JSON.stringify(userFromDb));
      localStorage.setItem("userId", userFromDb.id);

      if (complianceAccepted && !userFromDb.termsAcceptedAt) {
        try {
          const attestation = buildComplianceAttestationPayload({ accepted: true });
          const updated = await RecordComplianceAttestation(userFromDb.id, attestation);
          if (updated) userFromDb = { ...userFromDb, ...updated };
          clearPendingCompliance();
        } catch (error) {
          writePendingCompliance({
            ...buildComplianceAttestationPayload({ accepted: true }),
            termsVersion: TERMS_VERSION,
          });
          console.warn("Compliance attestation deferred:", error);
        }
      }

      AuthCtx.authenticateUser(userFromDb);

      window.location.href = "/shop";
    } catch (error) {
      console.error("Error during login process:", error);
    }
  }

  /**
   * Custom button (no iframe) intentionally avoids Google's personalized
   * "Continue as <name>" pill, which forces a white background regardless of theme.
   */
  const handleGoogleLogin = useGoogleLogin({
    flow: "implicit",
    scope: "openid email profile",
    onSuccess: async ({ access_token: accessToken }) => {
      try {
        await completeLogin(accessToken);
      } catch (err) {
        console.error("Google login failed:", err);
      }
    },
    onError: () => console.log("Login Failed"),
  });

  return (
    <Container maxW="container.xl" px={{ base: 4, md: 6, lg: 8 }}>
      <Box className="landing-hero">
        <Box className="landing-content">
          <Flex
            direction={{ base: "column", lg: "row" }}
            align={{ base: "stretch", lg: "center" }}
            gap={{ base: 12, lg: 16 }}
            pt={{ base: 4, md: 8 }}
          >
            <VStack align={{ base: "stretch", lg: "flex-start" }} spacing={6} flex="1" maxW="640px">
              <Box
                as="span"
                display="inline-flex"
                alignItems="center"
                gap={2}
                px={4}
                py={1.5}
                borderRadius="full"
                bg={tagBg}
                border="1px solid"
                borderColor={tagBorder}
                fontSize="sm"
                color={tagText}
                w="fit-content"
              >
                <Box
                  w={1.5}
                  h={1.5}
                  borderRadius="full"
                  bgGradient="linear(to-br, brand.400, brand.600)"
                  boxShadow={liveDotShadow}
                />
                {t("landing.communityTag")}
              </Box>

              <Heading
                as="h1"
                fontSize={{ base: "2.5rem", md: "3.25rem", lg: "3.75rem" }}
                lineHeight="1.05"
                fontWeight="700"
                color={headingColor}
                letterSpacing="-0.03em"
              >
                {t("landing.h1Part1")}{" "}
                <Box as="span" fontFamily={APP_FONT_FAMILY} fontWeight="400" fontStyle="italic" color={serifWord}>
                  {t("landing.h1Rwandan")}
                </Box>
                {t("landing.h1Diaspora") ? (
                  <>
                    {" "}
                    <Box as="span" fontFamily={APP_FONT_FAMILY} fontWeight="400" fontStyle="italic" color={serifWord}>
                      {t("landing.h1Diaspora")}
                    </Box>
                  </>
                ) : null}
                <Box
                  as="span"
                  display="block"
                  mt={{ base: 1.5, md: 2 }}
                  fontSize="0.88em"
                  lineHeight="1.12"
                >
                  {t("landing.h1AccentLead") ? (
                    <Box
                      as="span"
                      bgGradient={headlineAccentGradient}
                      bgClip="text"
                      fontWeight="700"
                      sx={{ WebkitTextFillColor: "transparent" }}
                    >
                      {t("landing.h1AccentLead")}
                    </Box>
                  ) : null}
                  {t("landing.h1AccentCulture") ? (
                    <>
                      {" "}
                      <Box
                        as="span"
                        bgGradient={headlineAccentGradient}
                        bgClip="text"
                        fontFamily={APP_FONT_FAMILY}
                        fontWeight="400"
                        fontStyle="italic"
                        sx={{ WebkitTextFillColor: "transparent" }}
                      >
                        {t("landing.h1AccentCulture")}
                      </Box>
                    </>
                  ) : null}
                  {t("landing.h1AccentMid") ? (
                    <>
                      {" "}
                      <Box
                        as="span"
                        bgGradient={headlineAccentGradient}
                        bgClip="text"
                        fontWeight="700"
                        sx={{ WebkitTextFillColor: "transparent" }}
                      >
                        {t("landing.h1AccentMid")}
                      </Box>
                    </>
                  ) : null}
                  {t("landing.h1AccentLove") ? (
                    <>
                      {" "}
                      <Box
                        as="span"
                        bgGradient={headlineAccentGradient}
                        bgClip="text"
                        fontWeight="700"
                        sx={{ WebkitTextFillColor: "transparent" }}
                      >
                        {t("landing.h1AccentLove")}
                      </Box>
                    </>
                  ) : null}
                </Box>
              </Heading>

              {t("landing.bodyLead") || t("landing.bodyMid") ? (
              <Text fontSize={{ base: "xs", md: "sm" }} color={subtext} lineHeight="tall" maxW="520px">
                {t("landing.bodyLead")}{" "}
                <Box as="span" fontStyle="italic">
                  {t("landing.bodyWorldwide")}
                </Box>
                . {t("landing.bodyMid")}{" "}
                <Box as="span" fontStyle="italic">
                  {t("landing.bodyBrand")}
                </Box>{" "}
                {t("landing.bodyTail")}{" "}
                <Box as="span" fontStyle="italic">
                  {t("landing.bodyValues")}
                </Box>
                .
              </Text>
              ) : null}

              <Box id="auth" w="100%" maxW="440px" scrollMarginTop="120px">
                <Box
                  p={{ base: 5, md: 6 }}
                  borderRadius="2xl"
                  bg={authCardBg}
                  backdropFilter="blur(16px)"
                  border="1px solid"
                  borderColor={authCardBorder}
                  boxShadow={authCardShadow}
                >
                  <Text fontSize="sm" color={authLabel} mb={4} fontWeight="medium">
                    {t("landing.continueGoogle")}
                  </Text>
                  <Checkbox
                    isChecked={complianceAccepted}
                    onChange={(e) => setComplianceAccepted(e.target.checked)}
                    alignItems="flex-start"
                    mb={4}
                    colorScheme="brand"
                    size="md"
                  >
                    <Text fontSize="xs" color={subtext} lineHeight="tall" pl={1}>
                      <Trans
                        i18nKey="landing.complianceCheckbox"
                        components={{
                          terms: (
                            <Link
                              as={RouterLink}
                              to="/terms#terms"
                              color={termsLinkColor}
                              fontWeight="600"
                              textDecoration="underline"
                              textUnderlineOffset="2px"
                            />
                          ),
                          privacy: (
                            <Link
                              as={RouterLink}
                              to="/terms#privacy"
                              color={termsLinkColor}
                              fontWeight="600"
                              textDecoration="underline"
                              textUnderlineOffset="2px"
                            />
                          ),
                          guidelines: (
                            <Link
                              as={RouterLink}
                              to="/terms#guidelines"
                              color={termsLinkColor}
                              fontWeight="600"
                              textDecoration="underline"
                              textUnderlineOffset="2px"
                            />
                          ),
                        }}
                      />
                    </Text>
                  </Checkbox>
                  <Button
                    type="button"
                    width="100%"
                    h="44px"
                    borderRadius="full"
                    variant="outline"
                    bg={googleBtnBg}
                    color={googleBtnColor}
                    borderColor={googleBtnBorder}
                    fontWeight="semibold"
                    leftIcon={<FcGoogle size={20} />}
                    isDisabled={!complianceAccepted}
                    _hover={{ bg: googleBtnHoverBg, borderColor: "brand.400" }}
                    _active={{ transform: "translateY(0)" }}
                    onClick={() => {
                      if (!complianceAccepted) {
                        toast({
                          title: t("landing.complianceRequired"),
                          status: "warning",
                          duration: 4000,
                          isClosable: true,
                          position: "top",
                        });
                        return;
                      }
                      writePendingCompliance(buildComplianceAttestationPayload({ accepted: true }));
                      handleGoogleLogin();
                    }}
                  >
                    {t("landing.continueGoogle")}
                  </Button>
                </Box>
              </Box>
            </VStack>

            <Box
              flex="1"
              maxW={{ base: "100%", lg: "480px" }}
              mx={{ base: "auto", lg: 0 }}
              w="100%"
              display={{ base: "none", lg: "block" }}
            >
              <LandingImage />
            </Box>
          </Flex>
        </Box>
      </Box>
    </Container>
  );
};

export default Landing;
